import { fetchJSON } from '@bonnet/next/fetch';

import { cmApiUrl } from '@atc/cm-api-url';
import { DuckSelector, objectDuckCreator } from '@atc/modular-redux';

import { vdpDuckPreset } from '@/ducks/presets/index.js';

/**
 * Redux duck for managing vehicle model information state in VDP (Vehicle Details Page).
 *
 * This duck handles the loading, storing, and retrieval of vehicle model specifications
 * and features. It provides selectors for accessing model info and initial load state,
 * and action creators for fetching model data from the API.
 *
 * @constant {Object} vdpModelInfoDuck
 * @property {string} store - The Redux store key ('modelInfo')
 * @property {Object} initialState - Initial state with empty info and initialLoad flag
 * @property {Object} selectors - Selectors for accessing state data
 * @property {Function} selectors.getInitialLoad - Gets initial load status
 * @property {Function} selectors.getModelInfo - Gets model information object
 * @property {Object} creators - Action creators for state mutations
 * @property {Function} creators.loadModelInfo - Async action to fetch model info from API
 * @property {Function} creators.setKeys - Action to update state keys
 * @property {Function} creators.clear - Action to clear/reset state
 */
const vdpModelInfoDuck = objectDuckCreator({
    ...vdpDuckPreset,
    store: 'modelInfo',
    initialState: {
        info: {},
        initialLoad: true,
    },
}).extend({
    selectors: () => ({
        getInitialLoad: new DuckSelector((selectors) => (state) => selectors.getDuckState(state).initialLoad),
        getModelInfo: new DuckSelector((selectors) => (state) => selectors.getDuckState(state).info),
    }),
    creators: () => ({
        loadModelInfo: ({ styleId }) => async (dispatch) => {
            if (!styleId) {
                return;
            }

            const {
                features = {},
                title = '',
            } = await fetchJSON(cmApiUrl(`/rest/lsc/modelinfo/${styleId}`), {
                circuitBreaker: {
                    id: 'lsc_moduleinfo',
                    timeout: 3000,
                    resetTimeout: 30000,
                    fallback: {},
                },
                headers: {
                    'X-fwd-svc': 'atc',
                },
            });
            await dispatch(vdpModelInfoDuck?.creators?.setKeys({
                info: {
                    features,
                    title,
                },
                initialLoad: false,
            }));
        },
    }),
});

export default vdpModelInfoDuck;
